import React, { useEffect, useRef, useState } from "react";
import { Layout, Tabs, Button, Spin, Modal } from "antd";
import { DownloadOutlined, PlusOutlined, StarOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/reducer/store";
import "./index.less";
import cehub from "@/resource/images/png/CEhub.png";
import syngenta from "@/resource/images/png/climateShift/syngenta.png";
import ClimateShiftLocation from "./components/ClimateShiftLocation/ClimateShiftLocation";
import HistogramOfYears from "./components/HistogramOfYears/HistogramOfYears";
import ExtTempTrends from "./components/ExtTempTrends/ExtTempTrends";
import FavouritesModal from "./components/FavouritesModal";
import AbbreviatedMap from "./components/AbbreviatedMap/AbbreviatedMap";
import {
    updateAddOrShowFavouritesModel,
    updateAuthorCode,
    updateCommonErrorMessage,
    updateCommonSuccessMessage,
    updateCommonWarnMessage,
    updateSelectedFavouriteItem,
} from "@/redux/actions/climateShiftAction";
import { ClimateShiftAddOrShowFavouritesEnum } from "@/util/cosntants";
import IF from "@/components/IF";
import PdfExportDemo from "./components/PdfExportDemo/PdfExportDemo";
// 若用AntD，需引入样式
import "antd/dist/reset.css";
import AverageDataChart from "./components/AverageDataChart/AverageDataChart";

const { Header, Content, Footer } = Layout;
const { TabPane } = Tabs;

/**
 * Main ClimateShift page component
 * Handles layout, search, chart display, and favourites logic
 */
const ClimateShift: React.FC = () => {
    const dispatch = useDispatch();

    // Redux state selectors
    const errorMessage = useSelector((state: RootState) => state.climateShiftReducer.errorMessage);
    const successMessage = useSelector((state: RootState) => state.climateShiftReducer.successMessage);
    const warningMessage = useSelector((state: RootState) => state.climateShiftReducer.warningMessage);
    const chartResult = useSelector((state: RootState) => state.climateShiftReducer.chartResult);
    const searchLoading = useSelector((state: RootState) => state.climateShiftReducer.searchLoading);
    const selectedLocationData = useSelector((state: RootState) => state.climateShiftReducer.selectedLocationData);
    const downloadAllLoading = useSelector((state: RootState) => state.climateShiftReducer.downloadAllLoading);

    // Tab state
    const [activeTab, setActiveTab] = useState("1");

    const [currentSelectedCountryCode, setCurrentSelectedCountryCode] = useState<string>("");
    const publicChildChangeCurrentSelectedCountryCode = (code: string) => {
        setCurrentSelectedCountryCode(code);
    };

    // Refs for charts
    const histogramRef = useRef<any>(null);
    const sub2CRef = useRef<any>(null);
    const sup15CRef = useRef<any>(null);
    const sub2CRefForPDF = useRef<any>(null);
    const sup15CRefForPDF = useRef<any>(null);
    const averageDataChartRef = useRef<any>(null);

    // Initialization: get author code from localStorage
    const initFunc = async () => {
        const tmpAuthorCode = localStorage.getItem("authorCode");
        if (tmpAuthorCode) {
            dispatch(updateAuthorCode(tmpAuthorCode));
        }
    };

    useEffect(() => {
        initFunc();
    }, []);

    // Notification modals for error, warning, success
    const errorNotification = (message: string) => {
        Modal.error({
            title: "Error",
            content: message,
            className: "custom-modal-ok-button",
        });
    };
    const warningNotification = (message: string) => {
        Modal.warning({
            title: "Warning",
            content: message,
            className: "custom-modal-ok-button",
        });
    };
    const successNotification = (message: string) => {
        Modal.success({
            title: "Success",
            content: message,
            className: "custom-modal-ok-button",
        });
    };

    // Listen for global messages and show notifications
    useEffect(() => {
        if (errorMessage) {
            errorNotification(errorMessage);
            dispatch(updateCommonErrorMessage(""));
        }
        if (warningMessage) {
            warningNotification(warningMessage);
            dispatch(updateCommonWarnMessage(""));
        }
        if (successMessage) {
            successNotification(successMessage);
            dispatch(updateCommonSuccessMessage(""));
        }
    }, [errorMessage, warningMessage, successMessage]);


    // Handle "Add to Favourites" button click
    const handleAddFavourites = () => {
        let tmpFlag = false;
        let tmpOptions = {};
        if (selectedLocationData) {
            tmpFlag = true;
            tmpOptions = {
                locationNameOrCoordinate: selectedLocationData?.adminName, // Use adminName as location identifier
                selectedLocationItem: selectedLocationData,
            };
        }
        // Dispatch add to favourites or show warning
        if (tmpFlag) {
            dispatch(updateSelectedFavouriteItem(tmpOptions));
            dispatch(updateAddOrShowFavouritesModel(ClimateShiftAddOrShowFavouritesEnum.ADD));
        }
        else {
            dispatch(updateCommonWarnMessage("Please select a location first."));
        }
    };

    return (
        // Main loading spinner for search
        <div id="climate-shift-component">
            <Spin spinning={searchLoading} tip="Loading...">
                <Layout className="climate-shift-layout">
                    {/* Header: logo and title (PC) */}
                    <Header className="climate-shift-header">
                        <div className="climate-shift-header-content">
                            <div className="header-left">
                                <img src={cehub} width={120} height={40}></img>
                                <span className="project-title">Climate Shift at Your Site</span>
                            </div>
                            <div className="header-right">
                                <img src={syngenta} ></img>
                            </div>
                        </div>
                    </Header>
                    {/* Header: mobile only */}
                    <Header className="mobile-header">
                        <span className="project-title">Climate Shift at Your Site</span>
                    </Header>
                    {/* Main area: search, map, charts, favourites */}
                    <div className="climate-shift-main-area">
                        {/* Search section */}
                        <div className="climate-shift-search-area">
                            <div className="search-area">
                                <div className="search-area-left">
                                    {/* Location search input and logic */}
                                    <ClimateShiftLocation
                                        childChangeCurrentSelectedCountryCode={publicChildChangeCurrentSelectedCountryCode}
                                    />
                                </div>
                                <div className="search-area-right">
                                    {/* Show favourites modal button */}
                                    <Button className="star-btn" onClick={() => {
                                        dispatch(updateAddOrShowFavouritesModel(ClimateShiftAddOrShowFavouritesEnum.SHOW));
                                    }}>
                                        <span className="star-btn-icon"><StarOutlined /></span>
                                    </Button>
                                    {/* Add to favourites button */}
                                    <Button className="fav-btn" onClick={() => {
                                        handleAddFavourites();
                                    }}>
                                        Add to Favourites
                                        <span className="fav-btn-icon"><PlusOutlined /></span>
                                    </Button>
                                </div>
                            </div>
                            {/* Abbreviated map display */}
                            <div className="abbreviated-map">
                                {/* Only show map if location data is available */}
                                <IF show={currentSelectedCountryCode !== undefined}>
                                    <AbbreviatedMap
                                        countryCode={currentSelectedCountryCode}
                                        lat={selectedLocationData?.lat}
                                        lng={selectedLocationData?.lng}
                                    />
                                </IF>
                            </div>
                        </div>
                        {/* Chart section with tabs */}
                        {(chartResult && chartResult.length > 0) && (
                            <Spin spinning={downloadAllLoading} tip="Loading...">
                                <Content className="climate-shift-content">
                                    <Tabs
                                        activeKey={activeTab}
                                        onChange={setActiveTab}
                                        className="climate-shift-tabs"
                                        tabBarExtraContent={
                                            <div className="tabs-extra">
                                                <PdfExportDemo
                                                    histogramRef={averageDataChartRef}
                                                    sup15CRef={sup15CRef}
                                                    sub2CRef={sub2CRef}
                                                    activeTab={activeTab}
                                                    onTabChange={setActiveTab}
                                                />
                                            </div>
                                        }>
                                        {/* Annual Temperature Histogram tab */}
                                        <TabPane
                                            tab={
                                                <>
                                                    <p className="tab-title-pc">Annual Temperature Histogram</p>
                                                    <p className="tab-title-mobile">Annual temp. Histogram</p>
                                                </>
                                            }
                                            key="1"
                                        >
                                            <div className="annual-chart-placeholder">
                                                {chartResult[0] && (
                                                    <HistogramOfYears
                                                        ref={histogramRef}
                                                        data={chartResult[0]}
                                                        activeKey={activeTab}
                                                    />
                                                )}
                                            </div>
                                            {/* 以下用于生成PDF中的Annual Temperature Histogram tab中的chart图，
                                            隐藏在页面中,在后台渲染，不影响布局 */}
                                            <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
                                                {chartResult[0] && (
                                                    <AverageDataChart
                                                        ref={averageDataChartRef}
                                                        data={chartResult[0]}
                                                        activeKey={activeTab}
                                                    />
                                                )}
                                            </div>
                                        </TabPane>
                                        {/* Extreme Temperature Trends tab */}
                                        <TabPane
                                            tab={
                                                <>
                                                    <p className="tab-title-pc">Extreme Temperature Trends</p>
                                                    <p className="tab-title-mobile">Extreme temp. trends</p>
                                                </>
                                            }
                                            key="2"
                                        >
                                            <div className="chart-row">
                                                <div className="extreme-chart-placeholder">
                                                    {chartResult[0] && (
                                                        <ExtTempTrends ref={sub2CRef}
                                                            data={chartResult[0]?.trendChartSub2C}
                                                            type="sub2C"
                                                            activeKey={activeTab} />
                                                    )}
                                                </div>
                                                <div className="extreme-chart-placeholder">
                                                    {chartResult[0] && (
                                                        <ExtTempTrends
                                                            ref={sup15CRef}
                                                            data={chartResult[0]?.trendChartSup15C}
                                                            type="sup15C"
                                                            activeKey={activeTab} />
                                                    )}
                                                </div>
                                            </div>
                                        </TabPane>
                                    </Tabs>
                                </Content>
                            </Spin>
                        )}
                        {/* Favourites modal */}
                        <FavouritesModal />
                    </div>
                    {/* Footer copyright section (commented out) */}
                    {/* <Footer className="climate-shift-footer">
                    <span>© Powered by FLASH Weather AI</span>
                    <span>Terms & Conditions</span>
                </Footer> */}
                </Layout>
            </Spin>
        </div>
    );
};

export default ClimateShift;